import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="max-w-[1320px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 space-y-8 animate-fade-up">
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium"
          >
            <span className="w-6 h-px bg-oxblood inline-block" />
            For wardrobe managers & stylists
          </span>

          <h1
            data-testid="hero-headline"
            className="font-display font-normal text-4xl sm:text-5xl lg:text-6xl xl:text-[68px] leading-[1.02] text-balance"
          >
            It's 11pm before a Vogue shoot.{" "}
            <span className="italic text-oxblood">No one</span> can find the
            Schiaparelli.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed"
          >
            Wardrobe Index is the operating system professional wardrobe
            managers use to catalog, locate, and move high-value pieces across
            racks, bins, archives, and client residences — without the
            spreadsheets.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <Button
              asChild
              size="lg"
              data-testid="hero-cta-primary"
              className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12 px-7 group"
            >
              <Link to="/signup" className="inline-flex items-center gap-2">
                Start 14-Day Free Trial
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <a
              href="#features"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center sm:justify-start gap-2 h-12 text-sm font-medium text-foreground hover:text-oxblood transition-colors"
            >
              See how it works
              <span aria-hidden>↓</span>
            </a>
          </div>

          <p className="text-xs text-muted-foreground tracking-wide">
            14-day free trial · No credit card required · Cancel anytime
          </p>
        </div>

        <div className="lg:col-span-6 relative animate-fade-up [animation-delay:120ms]">
          <div className="relative aspect-[4/5] lg:aspect-[3/4] w-full overflow-hidden bg-bone">
            <img
              src="https://images.unsplash.com/photo-1558769132-cb1aea458c5e?auto=format&fit=crop&w=1400&q=80"
              alt="A curated luxury garment rack inside a private styling studio."
              loading="eager"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/15 via-transparent to-transparent pointer-events-none" />
          </div>

          <div className="hidden md:flex absolute -bottom-6 -left-6 lg:-left-10 bg-background border border-border px-5 py-4 gap-4 items-center shadow-[0_24px_60px_-24px_rgba(33,31,28,0.18)]">
            <div className="w-10 h-10 bg-oxblood/10 grid place-items-center">
              <span className="font-display text-oxblood text-lg">A.</span>
            </div>
            <div className="text-left">
              <p className="font-display text-sm leading-tight">
                Alaïa wool coat
              </p>
              <p className="text-xs text-muted-foreground">
                Rack 3 · Studio · Last seen 2h ago
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
